import { useEffect, useState } from 'react';
import { useParams } from '@tanstack/react-router';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  LoadingSkeleton,
  Select,
} from '@nexora/design-system';
import { useBoms, type Bom } from './useBoms';
import { useBomCosts } from './useBomDetails';
import { formatCurrency } from '../../shared/utils/format';

/* ── Contrato (backend: bom-details.controller.ts + manufacturing-extended.dto.ts) ──
 *
 * GET /manufacturing/boms/:id/costs -> BomCostBreakdownResponseDto
 *   { bomId, version, lines[{ name, type (MATERIAL|LABOR), unitCost, qtyOrTime, totalCost }],
 *     materialCost, laborCost, totalCost, currency? }
 *
 * Tela prototipo "BOM - Custos Detalhados": Detailed Cost Breakdown com colunas
 * Component/Operation | Type | Unit Cost | Qty/Time | Total Cost.
 *
 * Fidelidade: a coluna "Margin Impact" do prototipo nao existe no DTO (o backend nao
 * calcula margem sobre o custo) — omitida, sem percentual inventado.
 */

const TYPE_VARIANT: Record<string, 'success' | 'warning' | 'neutral' | 'info'> = {
  MATERIAL: 'info',
  LABOR: 'warning',
};

const TYPE_LABEL: Record<string, string> = {
  MATERIAL: 'Material',
  LABOR: 'Mão de Obra',
};

export function BomCostsPage(): JSX.Element {
  const params = useParams({ strict: false }) as Record<string, string | undefined>;
  const routeId = params['bomId'] ?? params['id'];

  const [selectedId, setSelectedId] = useState<string>(routeId ?? '');
  useEffect(() => {
    if (routeId) setSelectedId(routeId);
  }, [routeId]);

  const { data: bomsData } = useBoms({ page: 1, limit: 100 });
  const boms = bomsData?.data ?? [];

  const { data: costs, isLoading, error, refetch } = useBomCosts(selectedId || undefined);

  return (
    <div className="bom-costs-page">
      <style>{`
        .bom-costs-page { padding: 1.5rem; }
        .bom-costs-table-wrap { overflow-x: auto; }
        .bom-costs-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .bom-costs-table th { text-align: left; padding: 0.75rem 1rem; font-weight: 600; color: #43474e; border-bottom: 1px solid #c4c6cf; background: #eceef0; white-space: nowrap; }
        .bom-costs-table td { padding: 0.75rem 1rem; border-bottom: 1px solid #e0e3e5; }
        .bom-costs-num { text-align: right; font-variant-numeric: tabular-nums; font-family: 'JetBrains Mono', ui-monospace, monospace; }
        .bom-costs-empty { padding: 2rem 1rem; text-align: center; color: #43474e; }
        .bom-costs-error { padding: 1.5rem; text-align: center; color: #ba1a1a; }
        .bom-costs-summary { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 1rem; margin-top: 1rem; }
        .bom-costs-summary-card { padding: 0.875rem 1rem; }
      `}</style>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <p className="font-label-sm text-label-sm text-on-surface-variant mb-xs uppercase tracking-wider">
            Produção / Estrutura de Produto (BOM)
          </p>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Custos Detalhados</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Breakdown de custos da estrutura: materiais e mão de obra por linha.
          </p>
        </div>
        {costs && (
          <Badge variant="neutral" size="md">
            v{costs.version}
          </Badge>
        )}
      </div>

      {!routeId && (
        <Card variant="outlined" padding="md" className="mb-lg">
          <div style={{ minWidth: '280px', maxWidth: '480px' }}>
            <Select
              label="Estrutura (BOM)"
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
            >
              <option value="">— Selecionar —</option>
              {boms.map((b: Bom) => (
                <option key={b.id} value={b.id}>
                  {b.description} (v{b.version})
                </option>
              ))}
            </Select>
          </div>
        </Card>
      )}

      {isLoading && (
        <Card padding="1rem">
          <LoadingSkeleton count={6} height="48px" />
        </Card>
      )}

      {error && !isLoading && (
        <Card padding="2rem">
          <div className="bom-costs-error" role="alert">
            <p>Erro ao carregar os custos detalhados</p>
            <p className="font-body-sm text-body-sm text-on-surface-variant">{(error as Error).message}</p>
            <Button size="sm" variant="outline" onClick={() => refetch()}>Tentar novamente</Button>
          </div>
        </Card>
      )}

      {!isLoading && !error && !costs && (
        <Card padding="2rem">
          <div className="bom-costs-empty">
            {selectedId ? 'Custos não encontrados para esta estrutura.' : 'Nenhuma estrutura selecionada.'}
          </div>
        </Card>
      )}

      {costs && (
        <Card padding="0">
          <CardHeader
            className="px-md pt-md"
            action={
              <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
                {costs.lines.length} {costs.lines.length === 1 ? 'linha' : 'linhas'}
              </span>
            }
          >
            <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">
              Detailed Cost Breakdown
            </h3>
          </CardHeader>
          {costs.lines.length === 0 ? (
            <div className="bom-costs-empty">
              Nenhuma linha de custo registrada nesta estrutura.
            </div>
          ) : (
            <div className="bom-costs-table-wrap">
              <table className="bom-costs-table">
                <thead>
                  <tr>
                    <th>Component / Operation</th>
                    <th>Type</th>
                    <th style={{ textAlign: 'right' }}>Unit Cost</th>
                    <th style={{ textAlign: 'right' }}>Qty / Time</th>
                    <th style={{ textAlign: 'right' }}>Total Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {costs.lines.map((line, idx) => (
                    <tr key={`${line.name}-${idx}`}>
                      <td className="font-medium text-on-surface">{line.name}</td>
                      <td>
                        <Badge variant={TYPE_VARIANT[line.type] ?? 'neutral'} size="sm">
                          {TYPE_LABEL[line.type] ?? line.type}
                        </Badge>
                      </td>
                      <td className="bom-costs-num">{formatCurrency(line.unitCost)}</td>
                      <td className="bom-costs-num">
                        {line.type === 'LABOR' ? `${line.qtyOrTime} h` : line.qtyOrTime}
                      </td>
                      <td className="bom-costs-num font-medium text-on-surface">
                        {formatCurrency(line.totalCost)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="px-md pb-md">
            <div className="bom-costs-summary">
              <div className="bom-costs-summary-card">
                <div className="font-label-sm text-label-sm text-on-surface-variant uppercase">Material</div>
                <div className="font-headline-md text-headline-md text-on-surface tabular-nums">
                  {formatCurrency(costs.materialCost)}
                </div>
              </div>
              <div className="bom-costs-summary-card">
                <div className="font-label-sm text-label-sm text-on-surface-variant uppercase">Mão de Obra</div>
                <div className="font-headline-md text-headline-md text-on-surface tabular-nums">
                  {formatCurrency(costs.laborCost)}
                </div>
              </div>
              <div className="bom-costs-summary-card">
                <div className="font-label-sm text-label-sm text-on-surface-variant uppercase">Custo Total</div>
                <div className="font-headline-md text-headline-md text-primary tabular-nums">
                  {formatCurrency(costs.totalCost)}
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}